import React, { useState } from "react";
import {
  Signature,
  Signatures,
} from "../../../../types/requestCreditDataTypes";

import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Grid,
  GridItem,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  useToast,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FiCheck, FiEdit2, FiPenTool, FiPlus, FiTrash2 } from "react-icons/fi";
import { useBranch } from "../../../../data/user";
import { DataTable, EmptyState } from "../../../ui";

interface signatureStepProps {
  savedData: Signatures;
  updateData:(data:Signatures)=> void;
}

interface UpdatedSignature extends Signature {
  index: number;
}

const SignatureStep: React.FC<signatureStepProps> = ({
  savedData,
  updateData,
}) => {
  const toast = useToast();
  const { branch } = useBranch();

  const [selectedUser, setSelectedUser] = useState<UpdatedSignature>({
    title: "",
    name: "",
    email: "",
    phone: "",
    mobile: "",
    index: -1,
  });

  const [editEnabled, setEditEnabled] = useState<boolean>(false);

  return (
    <Box>
      {savedData && savedData.length > 0 ? (
        <DataTable size="sm" mb={6} label="Signatories table">
          <Table>
            <Thead>
              <Tr>
                <Th scope="col">Title</Th>
                <Th scope="col">Name</Th>
                <Th scope="col">Email</Th>
                <Th scope="col">Phone</Th>
                <Th scope="col">Mobile</Th>
                <Th scope="col">
                  <VisuallyHidden>Actions</VisuallyHidden>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {savedData.map((user, index) => (
                <Tr key={index}>
                  <Td>{user.title}</Td>
                  <Td fontWeight={600}>{user.name}</Td>
                  <Td>{user.email}</Td>
                  <Td>{user.phone}</Td>
                  <Td>{user.mobile}</Td>

                  <Td>
                    <HStack spacing={2} justify="flex-end">
                      <Button
                        variant="secondary"
                        size="xs"
                        leftIcon={<FiEdit2 />}
                        onClick={() => {
                          setSelectedUser({ ...user, index: index });
                          setEditEnabled(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger-soft"
                        size="xs"
                        leftIcon={<FiTrash2 />}
                        onClick={() => {
                          updateData(savedData.filter((_, i) => i !== index));
                        }}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      ) : (
        <EmptyState
          icon={FiPenTool}
          title="No signatures added"
          description='Fill in the form below and press "Add" to list an authorised signatory.'
          py={6}
          headingAs="p"
        />
      )}
      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={5}>
        <GridItem colSpan={{ base: 1, md: 3 }}>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.name} // validation
              value={selectedUser?.name}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  name: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Title</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.title} // validation
              value={selectedUser?.title}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  title: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              isInvalid={!selectedUser?.email} // validation
              value={selectedUser?.email}
              placeholder={
                branch === "UAE" ? "test@test.com" : "Enter email address"
              }
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  email: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Phone </FormLabel>
            <Input
              isInvalid={!selectedUser?.phone} // validation
              value={selectedUser?.phone}
              placeholder={branch === "UAE" ? "041234567" : "Enter phone number"}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  phone: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Mobile </FormLabel>
            <Input
              value={selectedUser?.mobile}
              placeholder={branch === "UAE" ? "0501234567" : "Enter mobile number"}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  mobile: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem colSpan={1} display="flex" alignItems="flex-end">
          <Button
            w="full"
            variant="secondary"
            leftIcon={editEnabled ? <FiCheck /> : <FiPlus />}
            isDisabled={
              !selectedUser?.name ||
              !selectedUser?.title ||
              !selectedUser?.email ||
              !selectedUser?.phone
            }
            onClick={() => {
              if (editEnabled) {
                // update the data and don't forget to remove the index  but after updating the data
                const { index, ...newUser } = selectedUser;
                updateData(
                  savedData.map((user, i) => (i === index ? newUser : user))
                );

                toast({
                  title: "Signature Updated",
                  description: "Signature has been updated successfully",
                  status: "warning",
                  duration: 9000,
                  isClosable: true,
                });

                setEditEnabled(false);
              } else {
                // remove the index before updating the data
                const { index, ...newUser } = selectedUser;
                updateData([...savedData, newUser]);

                toast({
                  title: "Signature Added",
                  description: "Signature has been added successfully",
                  status: "success",
                  duration: 9000,
                  isClosable: true,
                });
              }
              setSelectedUser({
                title: "",
                name: "",
                email: "",
                phone: "",
                mobile: "",
                index: -1,
              });
            }}
          >
            {editEnabled ? "Save" : "Add"}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default SignatureStep;
